// Everything lives inside this IIFE, so the widget adds no globals instead of
// declaring ~60 top-level names.
//
// The theme editor re-renders the section on every setting change, and
// assets/theme-editor.js re-injects this script so it runs again against the
// fresh markup. As top-level declarations, that second run threw a
// redeclaration SyntaxError and left the widget dead until a page refresh.
// Each run now tears down the previous run first — its document-level hooks
// would otherwise keep acting on the old, detached form. The handoff is a
// document event rather than a global: every run listens for psg:teardown,
// and a new run fires it before binding anything of its own.
(() => {
  document.dispatchEvent(new CustomEvent('psg:teardown'));

  const sizeGuideEl = document.getElementById('product-size-guide-data');
  const sizeGuideForm = document.getElementById('product-size-guide-form');
  if (!sizeGuideForm) {
    console.error('[psg] Size guide script loaded without its form (#product-size-guide-form) — widget not started.');
    return;
  }

  // The footer's forward button drives every step, and is the "Start over"
  // recovery on the fallback card — without it the widget can't run at all.
  const sizeGuideNextBtn = sizeGuideForm.querySelector('.psg__btn--next');
  const sizeGuideNextLabel = sizeGuideNextBtn && sizeGuideNextBtn.querySelector('.psg__btn-label');
  if (!sizeGuideNextLabel) {
    console.error('[psg] Size guide markup is incomplete (footer .psg__btn--next / .psg__btn-label missing) — widget not started.');
    return;
  }

  // Every listener and observer this run attaches is bound to this signal,
  // so aborting it removes all of them at once.
  const lifecycle = new AbortController();
  const { signal } = lifecycle;
  document.addEventListener('psg:teardown', () => lifecycle.abort(), { signal });

  // Matches every option group the options panel renders (flap pills, color
  // swatches) — the set the step gate and the Add to Cart validator check.
  const OPTION_GROUP_SELECTOR = '.psg__option-group[data-option-name], .psg__color-swatches[data-option-name]';

  // Option-name aliases, lowercase. Mirrored in the section Liquid and the
  // data snippet (kept in sync manually).
  const FLAP_OPTION_ALIASES = ['flap type', 'flap configuration'];
  const COLOR_OPTION_ALIASES = ['color', 'colour'];

  // ── Widget state ─────────────────────────────────────────────────────
  // Tab 1's recommendation (drives Tab 3 color filtering and Tab 4).
  let recommendedVariant = null;
  // Tab 3 selections, keyed by option name. Widget-local: the page's native
  // variant picker is only touched at the end of the flow (Tab 4).
  const sizeGuideSelections = {};
  // Tab 4 quantity.
  let atcQty = 1;

  // Coerce a value that should be numeric into a number, or null when it
  // isn't one. Necessary because Shopify emits DECIMAL metafield/metaobject
  // values as strings ("20.8125") while integer metafields arrive as real
  // numbers — the snippet's | json faithfully preserves that inconsistency,
  // and every numeric check in this file (typeof === 'number', arithmetic,
  // formatting) needs one canonical type.
  function toNumberOrNull(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  // Normalize the parsed payload in place: all fields the widget does math or
  // type-checks on become number-or-null, whatever Liquid delivered.
  function normalizeSizeGuideData(data) {
    const numericFields = [
      'price',
      'max_pet_height', 'min_pet_height', 'min_pet_weight',
      'max_step', 'min_step',
      'min_compatible_thickness', 'max_compatible_thickness',
    ];
    data.variants.forEach((variant) => {
      numericFields.forEach((field) => {
        variant[field] = toNumberOrNull(variant[field]);
      });
      ['flap', 'cutout', 'frame'].forEach((dim) => {
        const obj = variant[dim];
        if (!obj || typeof obj !== 'object') {
          variant[dim] = null;
          return;
        }
        ['width', 'height', 'thickness'].forEach((axis) => {
          if (axis in obj) obj[axis] = toNumberOrNull(obj[axis]);
        });
      });
    });
    return data;
  }

  // Parse the embedded variant JSON defensively. A malformed payload (missing
  // snippet, or a metafield emitting an unexpected type) must degrade to the
  // widget's "Content not available" card — not die here with an uncaught
  // exception that leaves fully-rendered-but-dead controls on the page.
  // sizeGuideDataOk gates init at the bottom of this file and restartWidget.
  let sizeGuideData = { variants: [] };
  let sizeGuideDataOk = false;
  try {
    if (!sizeGuideEl) throw new Error('#product-size-guide-data not found');
    const parsed = JSON.parse(sizeGuideEl.textContent);
    if (!parsed || !Array.isArray(parsed.variants)) throw new Error('payload has no variants array');
    sizeGuideData = normalizeSizeGuideData(parsed);
    sizeGuideDataOk = true;
  } catch (e) {
    console.error('[psg] Widget data failed to load — showing the fallback card.', e);
  }

  const sizeGuideResult = document.getElementById('product-size-guide-result');
  const sizeGuideBackBtn = sizeGuideForm.querySelector('.psg__btn--back');

  function heightSortKey(variant) {
    return variant.max_pet_height == null ? Infinity : variant.max_pet_height;
  }

  // Filter to matching variants by size inputs in the size guide widget
  function findSize(height, width, limited, data) {

    const variants = data.variants;
    let choices = variants.filter(v => {
        // Limited mobility drops the usable opening by the step-over spread
        // (high minus low mount). Only adjust when BOTH step-over values are
        // present — a half-populated variant must not silently over-subtract.
        let step = 0;
        if (limited && v.max_step != null && v.min_step != null) {
          step = v.max_step - v.min_step;
        }
        return !height || height <= (v.max_pet_height - step);
      })
      .filter(v => !width || (v.flap && width <= v.flap.width))
      // Variants missing height data sort last. A width-only search doesn't
      // filter on height, and a null would otherwise sort as 0 and win.
      .sort((a, b) => heightSortKey(a) - heightSortKey(b));

    return choices;
  }

  // Defensive HTML escaping for any text that lands in innerHTML
  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, ch => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[ch]));
  }

  // Build a friendly summary of the inputs the user provided.
  // Width-only is valid (smallest variant whose flap is wide enough), so
  // the detail line should mention only the dimensions that were actually set.
  // Uses the option's display text (e.g. `18"`, `less than 18"`) so the
  // "less than X" options read naturally without hard-coded thresholds.
  function describeInputs({ heightLabel, widthLabel, limited }) {
    const parts = [];
    if (heightLabel) parts.push(`${heightLabel} height`);
    if (widthLabel) parts.push(`${widthLabel} width`);
    let str = `Based on ${parts.join(' × ')}`;
    if (limited) str += ' (limited mobility)';
    return str;
  }

  // ── Tape-measure formatters for the dimensions tab ───────────────────
  // Inch measurements are stored as decimals but read as fractions to match
  // a physical tape measure. Round to the nearest 1/32" (our finest expected
  // precision), then reduce to lowest terms so the denominator climbs only
  // as high as the value actually requires:
  //   20.8125 → "20-13/16"
  //        2 → "2"
  //      0.5 → "1/2"
  //   0.0625 → "1/16"
  function fmtInches(n) {
    if (n == null || typeof n !== 'number' || isNaN(n)) return '—';

    const negative = n < 0;
    const totalThirtySeconds = Math.round(Math.abs(n) * 32);
    const whole = Math.floor(totalThirtySeconds / 32);
    let numerator = totalThirtySeconds % 32;

    if (numerator === 0) return `${negative ? '-' : ''}${whole}`;

    // All reduced denominators are powers of 2 (since we started at /32)
    let denominator = 32;
    while (numerator % 2 === 0) {
      numerator /= 2;
      denominator /= 2;
    }
    const fraction = `${numerator}/${denominator}`;
    if (whole === 0) return `${negative ? '-' : ''}${fraction}`;
    return `${negative ? '-' : ''}${whole}-${fraction}`;
  }

  function fmtDim(w, h) {
    if (w == null || h == null) return '—';
    return `${fmtInches(w)}" W × ${fmtInches(h)}" H`;
  }

  function fmtRange(min, max) {
    if (min == null && max == null) return '—';
    if (min == null) return `up to ${fmtInches(max)}"`;
    if (max == null) return `${fmtInches(min)}" or more`;
    // Ensure smaller value reads first regardless of metafield labeling
    if (min > max) [min, max] = [max, min];
    return `${fmtInches(min)}" to ${fmtInches(max)}"`;
  }

  // Populate any [data-spec] target inside the widget with a value
  function setSpec(key, value) {
    const el = sizeGuideForm.querySelector(`[data-spec="${key}"]`);
    if (el) el.textContent = value;
  }

  // Fill (or reset) the Dimensions tab with the recommended variant's specs.
  // Called from renderResult — keeps Tab 2 in sync with whatever Tab 1 recommended.
  function updateDimensionsTab(variant) {
    if (!variant) {
      setSpec('size-display', '—');
      setSpec('flap-size', '—');
      setSpec('cutout-size', '—');
      setSpec('frame-size', '—');
      setSpec('thickness', '—');
      return;
    }

    setSpec('size-display', variant.size || '—');
    setSpec('flap-size',   variant.flap   ? fmtDim(variant.flap.width,   variant.flap.height)   : '—');
    setSpec('cutout-size', variant.cutout ? fmtDim(variant.cutout.width, variant.cutout.height) : '—');
    setSpec('frame-size',  variant.frame  ? fmtDim(variant.frame.width,  variant.frame.height)  : '—');
    setSpec('thickness',   fmtRange(variant.min_compatible_thickness, variant.max_compatible_thickness));
  }

  // SVG path data for the result card's icon, one per card state.
  const RESULT_ICONS = {
    match: '<path d="M20 6L9 17l-5-5"/>',
    noMatch: '<path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>',
    unavailable: '<circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/>',
  };

  // Write one state of the result card. The three text lines are plain text
  // and escaped here; icon is a key of RESULT_ICONS. The card lives in the
  // pet_sizing block, which per-block gating can omit — every write must
  // tolerate its absence.
  function renderResultCard({ icon, muted, label, size, detail }) {
    if (!sizeGuideResult) return;
    sizeGuideResult.className = muted ? 'psg__result psg__result--none' : 'psg__result';
    sizeGuideResult.innerHTML = `
      <div class="psg__result-icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          ${RESULT_ICONS[icon]}
        </svg>
      </div>
      <div class="psg__result-text">
        <div class="psg__result-label">${escapeHtml(label)}</div>
        <div class="psg__result-size">${escapeHtml(size)}</div>
        <div class="psg__result-detail">${escapeHtml(detail)}</div>
      </div>
    `;
  }

  // Render the recommendation (or no-match state) into the result card
  // AND propagate the variant to downstream tabs (Dimensions, etc.)
  function renderResult(variant, inputs) {
    if (!variant) {
      renderResultCard({
        icon: 'noMatch',
        muted: true,
        label: 'No match on this product',
        size: 'Try a larger product line',
        detail: `No size of this product fits ${describeInputs(inputs).replace(/^Based on /, '')}.`,
      });
    } else {
      renderResultCard({
        icon: 'match',
        label: 'Recommended size',
        size: variant.size || variant.title,
        detail: describeInputs(inputs),
      });
    }
    updateDimensionsTab(variant || null);
  }

  // The panel currently on screen (a block panel or the fallback card).
  function getActivePanel() {
    return sizeGuideForm.querySelector('.psg__panel:not(.psg__panel--hidden)');
  }

  // Per-block gating means "the first tab" isn't necessarily Pet Sizing —
  // identify the pet-sizing panel by its height input, not by index 0.
  function activePanelIsPetSizing() {
    const panel = getActivePanel();
    return !!(panel && panel.querySelector('#product-size-guide-height'));
  }

  // The pet-sizing tab is "activated" once the user provides height OR width.
  // The limited-mobility checkbox is a modifier only — it doesn't activate.
  // Element-based (not FormData) so a gated-out pet_sizing block reads as
  // "no input" rather than FormData.get(...) returning null !== ''.
  function tab1HasInput() {
    const heightSelect = sizeGuideForm.querySelector('#product-size-guide-height');
    const widthSelect = sizeGuideForm.querySelector('#product-size-guide-width');
    return !!((heightSelect && heightSelect.value !== '') || (widthSelect && widthSelect.value !== ''));
  }

  // Options-style (sub-)panels gate forward progression the same way Tab 1's
  // inputs do: the user must commit a choice before Next activates. Returns the
  // option name (e.g. "Flap Type", "Color") still awaiting a selection on the
  // currently visible panel or sub-panel — or null when nothing blocks. Panels
  // without option groups (Pet Sizing, Dimensions, Add to Cart) never block.
  function getPendingSelectionName() {
    const currentPanel = getActivePanel();
    if (!currentPanel) return null;

    // A sub-tab short of the last gates only on its own choice. Leaving the
    // panel — from its last sub-tab, or a panel without sub-tabs — needs EVERY
    // choice on it: sub-tabs are clickable, so a later one can be reached
    // without making an earlier one. Groups are checked in DOM order, so the
    // label names the first missing choice.
    let root = currentPanel;
    const subtabs = getCurrentPanelSubtabs();
    const activeIdx = getActiveSubtabIndex(subtabs);
    if (activeIdx >= 0 && activeIdx < subtabs.length - 1) {
      const name = subtabs[activeIdx].dataset.subtab;
      root = currentPanel.querySelector(`.psg__subpanel[data-subtab="${name}"]`) || currentPanel;
    }

    const pending = Array.from(root.querySelectorAll(OPTION_GROUP_SELECTOR))
      .find(group => !sizeGuideSelections[group.dataset.optionName]);
    return pending ? pending.dataset.optionName : null;
  }

  // Pick the right button label + muted state based on current tab.
  //   Last tab              → "Start over"    (data-label-restart)
  //   Pet sizing, no input  → "Enter pet size" + muted   (data-label-empty)
  //   Pet sizing, has input → "Get my size"   (data-label-default)
  //   Options, unselected   → "Select <option>" + muted  (composed)
  //   Other tabs            → "Next step"     (data-label-next)
  function updateButtonLabel() {
    const currentIndex = getCurrentTabIndex();
    const totalTabs = sizeGuideForm.querySelectorAll('.psg__tab').length;
    const isLastTab = (currentIndex === totalTabs - 1);

    let label;
    let muted = false;
    let restart = false;
    if (fallbackPanelActive()) {
      // Error card is showing (no active tab) — the footer button becomes the
      // recovery control regardless of tab math.
      label = sizeGuideNextLabel.dataset.labelRestart;
      restart = true;
    } else if (isLastTab && totalTabs > 1) {
      // The panel carries its own Add to cart CTA, so the footer button becomes
      // a "Start over" control rather than a duplicate add-to-cart.
      label = sizeGuideNextLabel.dataset.labelRestart;
      restart = true;
    } else if (activePanelIsPetSizing()) {
      if (tab1HasInput()) {
        label = sizeGuideNextLabel.dataset.labelDefault;
      } else {
        label = sizeGuideNextLabel.dataset.labelEmpty;
        muted = true;
      }
    } else {
      const pending = getPendingSelectionName();
      if (pending) {
        label = `Select ${pending.toLowerCase()}`;
        muted = true;
      } else {
        label = sizeGuideNextLabel.dataset.labelNext;
      }
    }
    sizeGuideNextLabel.textContent = label;
    sizeGuideNextBtn.classList.toggle('psg__btn--muted', muted);
    sizeGuideNextBtn.classList.toggle('psg__btn--restart', restart);
    sizeGuideNextBtn.hidden = false;
  }

  // Deactivate every step indicator, panel, and progress dot — the shared
  // first half of showing any panel.
  function clearActiveStep() {
    sizeGuideForm.querySelectorAll('.psg__tab').forEach(t => {
      t.classList.remove('psg__tab--active');
      t.removeAttribute('aria-current');
    });
    sizeGuideForm.querySelectorAll('.psg__panel').forEach(p => p.classList.add('psg__panel--hidden'));
    sizeGuideForm.querySelectorAll('.psg__dot').forEach(d => d.classList.remove('psg__dot--active'));
  }

  // The "Content not available" error card — a non-block panel rendered by the
  // section as a safety net (like a 404 page). Shown when navigation lands on a
  // tab whose panel is missing/broken; the footer button reads "Start over"
  // while it's active, which is the recovery path (restartWidget → gotoTab(0)).
  function showFallbackPanel() {
    const fallback = sizeGuideForm.querySelector('.psg__panel--fallback');
    if (!fallback) return false;

    clearActiveStep();
    fallback.classList.remove('psg__panel--hidden');
    if (sizeGuideBackBtn) sizeGuideBackBtn.disabled = true;
    updateButtonLabel();
    return true;
  }

  function fallbackPanelActive() {
    const fallback = sizeGuideForm.querySelector('.psg__panel--fallback');
    return !!(fallback && !fallback.classList.contains('psg__panel--hidden'));
  }

  // Move between tabs — looks up panels and tab buttons by data-tab-index
  function gotoTab(targetIndex) {
    const targetTab = sizeGuideForm.querySelector(`.psg__tab[data-tab-index="${targetIndex}"]`);
    const targetPanel = sizeGuideForm.querySelector(`.psg__panel[data-tab-index="${targetIndex}"]`);
    // Past the end (neither exists) is a benign no-op; a tab WITHOUT its panel
    // (or vice versa) means the widget markup is broken — show the error card.
    if (!targetTab && !targetPanel) return false;
    if (!targetTab || !targetPanel) return showFallbackPanel();

    clearActiveStep();

    // Activate target
    targetTab.classList.add('psg__tab--active');
    targetTab.setAttribute('aria-current', 'step');
    targetPanel.classList.remove('psg__panel--hidden');
    const targetDot = sizeGuideForm.querySelector(`.psg__dot[data-tab-index="${targetIndex}"]`);
    if (targetDot) targetDot.classList.add('psg__dot--active');

    // Back button only enabled if not on the first tab
    if (sizeGuideBackBtn) sizeGuideBackBtn.disabled = (targetIndex === 0);

    // Entering the Add to Cart panel → sync the summary + price to current state
    if (targetPanel.querySelector('.psg__atc')) updateAddToCartTab();

    // Refresh the Next/Add to cart label for the new tab
    updateButtonLabel();

    // Footer-driven navigation may leave the user scrolled mid-panel — bring
    // the tab nav back into view so the new tab's position in the flow is clear.
    scrollWidgetNavIntoView();

    return true;
  }

  function getCurrentTabIndex() {
    const activeTab = sizeGuideForm.querySelector('.psg__tab--active');
    return activeTab ? Number(activeTab.dataset.tabIndex) : 0;
  }

  // Keep the widget's tab nav visible when footer navigation changes the
  // tab/sub-tab. "Visible" means below the page's sticky stack (theme header +
  // product nav bar), which covers the top of the viewport once scrolled. The
  // offset is measured live from the sticky product nav when it's stuck;
  // otherwise fall back to the same 210px constant that bar uses for its own
  // section scrolling. Only scrolls when the nav is actually hidden — never
  // jumps a page that's already showing it.
  function scrollWidgetNavIntoView() {
    const nav = sizeGuideForm.querySelector('.psg__nav');
    if (!nav) return;

    // Hosted inside a modal → the scroll container is the modal window,
    // not the page; the page's sticky stack is irrelevant behind the overlay.
    const modalWindow = sizeGuideForm.closest('.modal__window');
    if (modalWindow) {
      const delta = nav.getBoundingClientRect().top - modalWindow.getBoundingClientRect().top - 16;
      if (delta < -4) {
        modalWindow.scrollTo({ top: modalWindow.scrollTop + delta, behavior: 'smooth' });
      }
      return;
    }

    let offset = 222;
    const stickyBar = document.querySelector('.sticky-nav-tabs-container');
    if (stickyBar) {
      const position = window.getComputedStyle(stickyBar).position;
      const rect = stickyBar.getBoundingClientRect();
      if ((position === 'fixed' || position === 'sticky') && rect.bottom > 0) {
        offset = rect.bottom + 24;
      }
    }

    const navTop = nav.getBoundingClientRect().top;
    if (navTop >= offset) return;

    window.scrollTo({
      top: window.pageYOffset + navTop - offset,
      behavior: 'smooth',
    });
  }

  // Sub-tabs (Tab 3's Flap Type / Color) get cycled before advancing the
  // main tab. Lets the footer Next/Back walk the full flow linearly:
  //   Tab 1 → Tab 2 → Tab 3:Flap → Tab 3:Color → Tab 4
  function getCurrentPanelSubtabs() {
    const currentPanel = getActivePanel();
    if (!currentPanel) return [];
    return Array.from(currentPanel.querySelectorAll('.psg__subtab'));
  }

  function getActiveSubtabIndex(subtabs) {
    return subtabs.findIndex(t => t.classList.contains('psg__subtab--active'));
  }

  // Past the last tab, gotoTab is a no-op — nothing to advance to.
  function advanceTab() {
    const subtabs = getCurrentPanelSubtabs();
    const activeIdx = getActiveSubtabIndex(subtabs);
    if (activeIdx >= 0 && activeIdx < subtabs.length - 1) {
      handleSubtabClick(subtabs[activeIdx + 1]);
      scrollWidgetNavIntoView();
      return;
    }

    gotoTab(getCurrentTabIndex() + 1);
  }

  // Clear Tab 3-style selections (option groups, color swatches, detail cards)
  // within a panel or sub-panel. Mutates sizeGuideSelections in lockstep with
  // the UI so Back functions as a true undo — no stale commitments survive a
  // rewind. Tab 1 inputs (height/width/limited) are untouched: they're typed
  // data, not commitments, and stay editable across navigation.
  function clearSelectionsInPanel(root) {
    root.querySelectorAll('.psg__option-group').forEach(group => {
      const name = group.dataset.optionName;
      if (name) delete sizeGuideSelections[name];
      group.querySelectorAll('.psg__option-btn').forEach(b => {
        b.classList.remove('psg__option-btn--selected');
        b.setAttribute('aria-pressed', 'false');
      });
    });
    root.querySelectorAll('.psg__option-detail').forEach(d => {
      d.classList.remove('psg__option-detail--selected');
      d.setAttribute('aria-pressed', 'false');
    });
    root.querySelectorAll('.psg__color-swatches').forEach(container => {
      const name = container.dataset.optionName;
      if (name) delete sizeGuideSelections[name];
      container.querySelectorAll('.psg__color-swatch').forEach(s => {
        s.setAttribute('aria-pressed', 'false');
      });
    });
    root.querySelectorAll('[data-color-selected]').forEach(el => {
      el.textContent = '—';
    });
  }

  function retreatTab() {
    const subtabs = getCurrentPanelSubtabs();
    const activeIdx = getActiveSubtabIndex(subtabs);
    const currentPanel = getActivePanel();

    if (activeIdx > 0) {
      // Step back within the same panel — undo the sub-panel we're leaving.
      const leavingName = subtabs[activeIdx].dataset.subtab;
      const leavingSubpanel = currentPanel && currentPanel.querySelector(`.psg__subpanel[data-subtab="${leavingName}"]`);
      if (leavingSubpanel) clearSelectionsInPanel(leavingSubpanel);
      renderColorSwatches();
      handleSubtabClick(subtabs[activeIdx - 1]);
      scrollWidgetNavIntoView();
      return;
    }

    // Leaving the panel entirely → clear EVERY selection on it, including
    // sub-panels the user may have touched via direct sub-nav click.
    if (currentPanel) clearSelectionsInPanel(currentPanel);
    renderColorSwatches();

    const prev = getCurrentTabIndex() - 1;
    if (prev < 0) return;
    gotoTab(prev);
  }

  // A catalog-data gap must not read as a sizing verdict. When the inputs the
  // user actually set have NO underlying data on ANY variant (sizing metafields
  // not yet populated on this product), the honest state is "data unavailable",
  // not "no size fits your pet — try a larger product".
  function sizingDataMissing(inputs) {
    const variants = sizeGuideData.variants;
    if (!variants.length) return true;
    if (inputs.height && !variants.some(v => typeof v.max_pet_height === 'number')) return true;
    if (inputs.width && !variants.some(v => v.flap && typeof v.flap.width === 'number')) return true;
    return false;
  }

  function renderDataUnavailable() {
    renderResultCard({
      icon: 'unavailable',
      muted: true,
      label: 'Sizing data unavailable',
      size: 'We can\'t calculate a fit for this product yet',
      detail: 'Please check the size chart on this page, or contact us for help choosing a size.',
    });
    updateDimensionsTab(null);
  }

  // "Start over" (the footer button on the last tab): wipe every input and
  // selection and return to Tab 1. Tab 1 inputs are form fields → form.reset()
  // clears them; widget-local selection state is cleared explicitly.
  function restartWidget() {
    // Broken data payload: there is nothing meaningful to restart into —
    // stay parked on the error card.
    if (!sizeGuideDataOk) {
      showFallbackPanel();
      return;
    }

    sizeGuideForm.reset();

    recommendedVariant = null;
    Object.keys(sizeGuideSelections).forEach((key) => delete sizeGuideSelections[key]);
    atcQty = 1;

    // Clear Tab 3-style selections (option buttons, detail cards, swatches) on
    // every panel, then reset each options panel back to its first sub-tab.
    sizeGuideForm.querySelectorAll('.psg__panel').forEach((panel) => clearSelectionsInPanel(panel));
    sizeGuideForm.querySelectorAll('.psg__options').forEach((opts) => {
      const firstSubtab = opts.querySelector('.psg__subtab');
      if (firstSubtab) handleSubtabClick(firstSubtab);
    });

    // Clear the result card + downstream tabs, then rebuild from the empty state.
    if (sizeGuideResult) sizeGuideResult.innerHTML = '';
    updateDimensionsTab(null);
    renderColorSwatches();
    updateAddToCartTab();

    gotoTab(0);
  }

  // ── Tab 3 selections ─────────────────────────────────────────────────
  // Read whichever entry in sizeGuideSelections matches one of the aliases.
  function getSelectionByAlias(aliases) {
    for (const key of Object.keys(sizeGuideSelections)) {
      if (aliases.includes(key.toLowerCase())) return sizeGuideSelections[key];
    }
    return null;
  }

  function getSelectedFlapType() {
    return getSelectionByAlias(FLAP_OPTION_ALIASES);
  }

  function getSelectedColor() {
    return getSelectionByAlias(COLOR_OPTION_ALIASES);
  }

  // Rebuild Tab 3's color swatches from the variants that match the current
  // recommendation + flap selection. Called on init, on recommendation change,
  // and on flap-type selection change. Colors with no matching variant are
  // silently omitted (defensive — relies on color being the last option).
  function renderColorSwatches() {
    const container = sizeGuideForm.querySelector('.psg__color-swatches');
    if (!container) return;

    const optionName = container.dataset.optionName;
    const recommendedSize = recommendedVariant ? recommendedVariant.size : null;
    const selectedFlap = getSelectedFlapType();

    const candidates = sizeGuideData.variants.filter(v => {
      if (v.color == null) return false;
      if (recommendedSize && v.size && v.size !== recommendedSize) return false;
      if (selectedFlap && v.flap_type && v.flap_type !== selectedFlap) return false;
      return true;
    });

    // First variant per color wins for the swatch image
    const colorMap = new Map();
    for (const v of candidates) {
      if (!colorMap.has(v.color)) colorMap.set(v.color, v);
    }

    const currentColor = sizeGuideSelections[optionName];
    const stillAvailable = currentColor && colorMap.has(currentColor);

    container.innerHTML = '';
    for (const [color, variant] of colorMap) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'psg__color-swatch';
      btn.dataset.colorValue = color;
      btn.setAttribute('aria-pressed', stillAvailable && color === currentColor ? 'true' : 'false');

      const imgWrap = document.createElement('span');
      imgWrap.className = 'psg__color-swatch-img';
      if (variant.featured_image) {
        const img = document.createElement('img');
        img.src = variant.featured_image;
        img.alt = color;
        img.loading = 'lazy';
        imgWrap.appendChild(img);
      }

      const label = document.createElement('span');
      label.className = 'psg__color-swatch-label';
      label.textContent = color;

      btn.appendChild(imgWrap);
      btn.appendChild(label);
      container.appendChild(btn);
    }

    if (currentColor && !stillAvailable) {
      delete sizeGuideSelections[optionName];
    }

    const display = sizeGuideForm.querySelector('[data-color-selected]');
    if (display) display.textContent = sizeGuideSelections[optionName] || '—';
  }

  function handleSubtabClick(subtab) {
    const optionsRoot = subtab.closest('.psg__options');
    if (!optionsRoot) return;
    const target = subtab.dataset.subtab;

    optionsRoot.querySelectorAll('.psg__subtab').forEach(t => {
      const isActive = t.dataset.subtab === target;
      t.classList.toggle('psg__subtab--active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    optionsRoot.querySelectorAll('.psg__subpanel').forEach(p => {
      p.classList.toggle('psg__subpanel--hidden', p.dataset.subtab !== target);
    });

    // The newly visible sub-panel may or may not have its selection made yet
    updateButtonLabel();
  }

  function handleSwatchClick(swatch) {
    const container = swatch.closest('.psg__color-swatches');
    if (!container) return;
    const optionName = container.dataset.optionName;
    const optionValue = swatch.dataset.colorValue;

    container.querySelectorAll('.psg__color-swatch').forEach(s => {
      s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false');
    });

    const display = sizeGuideForm.querySelector('[data-color-selected]');
    if (display) display.textContent = optionValue;

    sizeGuideSelections[optionName] = optionValue;
    updateButtonLabel();
  }

  // Apply a flap-type selection by value. Drives BOTH the pill buttons and the
  // description cards below them so either control reflects the same choice —
  // a user can click the "Single"/"Double" pill or its descriptor card.
  function selectOptionValue(group, optionValue) {
    if (!group) return;
    const optionName = group.dataset.optionName;

    group.querySelectorAll('.psg__option-btn').forEach(b => {
      const on = b.dataset.optionValue === optionValue;
      b.classList.toggle('psg__option-btn--selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    const subpanel = group.closest('.psg__subpanel');
    if (subpanel) {
      subpanel.querySelectorAll('.psg__option-detail').forEach(d => {
        const on = d.dataset.optionValue === optionValue;
        d.classList.toggle('psg__option-detail--selected', on);
        d.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    sizeGuideSelections[optionName] = optionValue;
    // Any non-color selection may narrow color availability
    renderColorSwatches();
    updateButtonLabel();
  }

  function handleOptionBtnClick(btn) {
    selectOptionValue(btn.closest('.psg__option-group'), btn.dataset.optionValue);
  }

  // A description card mirrors a pill button — resolve its option group (the
  // card lives in a sibling container) and apply the same selection.
  function handleOptionDetailClick(detail) {
    const subpanel = detail.closest('.psg__subpanel');
    const group = subpanel && subpanel.querySelector('.psg__option-group');
    selectOptionValue(group, detail.dataset.optionValue);
  }

  // ── Tab 4 (Add to Cart) ──────────────────────────────────────────────
  // Summary + price reflect the widget's own selection state. "Save and
  // continue" commits the chosen variant to the page's native variant picker
  // (see commitToVariantPicker); "Add to cart" commits it and then adds it
  // through the theme's product form (see addToCartViaForm).

  // Format integer cents into the store's currency. Falls back to a plain
  // dollar string if Intl can't resolve the currency code.
  function formatMoney(cents) {
    if (cents == null) return '—';
    const currency = sizeGuideData.currency || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // Most specific variant matching the current size + flap + color selections.
  // Filters are skipped when a selection is absent, so price still resolves
  // (to the first surviving variant) before every option has been chosen.
  function getAtcVariant() {
    const size = recommendedVariant ? recommendedVariant.size : null;
    const flap = getSelectedFlapType();
    const color = getSelectedColor();
    const matches = sizeGuideData.variants.filter(v => {
      if (size && v.size && v.size !== size) return false;
      if (flap && v.flap_type && v.flap_type !== flap) return false;
      if (color && v.color && v.color !== color) return false;
      return true;
    });
    return matches[0] || null;
  }

  // Clamp to 1–99: no zero/negative/NaN states, and no absurd quantities that
  // only exist to trip Shopify's inventory rejection on submit.
  function setAtcQty(n) {
    n = parseInt(n, 10);
    if (!Number.isFinite(n)) n = 1;
    atcQty = Math.min(99, Math.max(1, n));
    updateAddToCartTab();
  }

  // Flip the Save button between its default outline and the filled "saved"
  // confirmation. Label text comes from the button's data-label-* attributes.
  function setSaveState(btn, saved) {
    btn.classList.toggle('psg__atc-save--saved', saved);
    const label = btn.querySelector('.psg__atc-save-label');
    if (label) {
      label.textContent = saved ? btn.dataset.labelSaved : btn.dataset.labelDefault;
    }
  }

  // Commit a resolved variant's option values to the theme's native
  // <variant-picker> so the product page reflects the selection (price, media,
  // variant id, URL) — WITHOUT adding to cart.
  //
  // Unlike the theme's applySearchParams, the radios are set SILENTLY (no
  // per-input change events) and a single change event is dispatched from the
  // picker afterwards, carrying the target variant id in event.detail. Reason:
  // each radio's data-variant-id is a Liquid-rendered prediction of "this value
  // + the other options as selected at render time". Native clicks change one
  // option at a time, so that prediction is always fresh — but this commit
  // changes several options in one synchronous burst, leaving every radio's
  // prediction stale. Per-input events would make variant-picker.js fetch by
  // those stale ids (handleVariantChange prefers evt.target.dataset.variantId),
  // and the last fetch — rendered for the WRONG variant — would win its
  // variantRequestId race and be applied to the page. The single picker-level
  // dispatch has no dataset.variantId, so the handler falls back to
  // evt.detail.variantId: one fetch, for exactly the intended variant.
  //
  // Options are matched by the selector's data-index (1-based option position),
  // so a variant's options array maps positionally with no alias lists involved.
  function commitToVariantPicker(variant) {
    if (!variant || !Array.isArray(variant.options)) return false;

    const picker = document.querySelector('.product-info variant-picker')
      || document.querySelector('variant-picker');
    if (!picker) return false;

    let committed = false;

    picker.querySelectorAll('.option-selector').forEach((selector) => {
      const index = parseInt(selector.dataset.index, 10) - 1;
      const desired = variant.options[index];
      if (desired == null) return;

      if (selector.dataset.selectorType === 'dropdown') {
        // Defensive path — this store uses "buttons", but keep dropdowns working.
        const opt = selector.querySelector(
          `.custom-select__option[data-value="${CSS.escape(desired)}"]:not([aria-selected="true"])`
        );
        const customSelect = opt && opt.closest('custom-select');
        if (customSelect) {
          const convertEvent = (evt) => {
            const detail = Object.assign({}, evt.detail, { noFocus: true });
            customSelect.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false, detail }));
          };
          customSelect.addEventListener('change', convertEvent, { once: true });
          customSelect.selectOption(opt);
          committed = true;
        }
      } else {
        const input = selector.querySelector(`input[value="${CSS.escape(desired)}"]`);
        if (input && !input.checked) {
          input.checked = true;
          // Keep the swatch label (": White") in sync, as applySearchParams does.
          const labelValue = selector.querySelector('.option-selector__label-value');
          if (labelValue) labelValue.textContent = desired;
          committed = true;
        }
      }
    });

    if (committed) {
      picker.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        cancelable: false,
        detail: { noFocus: true, variantId: variant.id },
      }));
    }

    return committed;
  }

  // Error line under the widget's Add to cart button. Message is set via
  // textContent (never innerHTML) — it can contain server-provided text.
  function showAtcError(message) {
    const el = sizeGuideForm.querySelector('[data-atc-error]');
    if (!el) return;
    el.textContent = message || 'Something went wrong adding this to your cart. Please try again.';
    el.hidden = false;
  }

  function clearAtcError() {
    const el = sizeGuideForm.querySelector('[data-atc-error]');
    if (el) el.hidden = true;
  }

  // Add a resolved variant to the cart by handing off to the theme's own
  // product form. product-form.js intercepts the form submit and runs the full
  // AJAX /cart/add → cart-drawer → cart-icon flow, so after-add behavior is
  // whatever the theme is configured to do. We set id + quantity directly (not
  // via the variant picker) so the synchronously-built FormData is correct
  // regardless of any in-flight variant-picker re-render.
  function addToCartViaForm(variant, qty) {
    if (!variant) return false;

    const form = document.querySelector('.js-product-form-main')
      || document.querySelector('.product-info .js-product-form')
      || document.querySelector('.js-product-form');
    if (!form) return false;

    const idInput = form.querySelector('[name="id"]');
    if (idInput) idInput.value = variant.id;

    const qtyInput = form.querySelector('[name="quantity"]');
    if (qtyInput) qtyInput.value = qty;

    // The native form may carry required fields the widget doesn't collect —
    // e.g. slider products' properties[TRACK] input. requestSubmit() would be
    // blocked by that validation with no visible feedback near the widget, so
    // fail loudly here instead of appearing to do nothing.
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      showAtcError('This product has required options the size guide can’t fill in. Please add it to your cart from the product options above.');
      return false;
    }

    // Line item property flag — records "purchased via size guide" on the order
    // line. Injected here (not in the markup) so it rides ONLY on adds the widget
    // triggers, never a normal native add-to-cart. The leading underscore keeps
    // it hidden from the customer in cart/checkout while still saving on the order.
    // product-form.js reads the form's FormData synchronously at submit start, so
    // we can remove the input right after submitting — it stays out of the form
    // for any subsequent native add-to-cart.
    const sizeGuideFlag = document.createElement('input');
    sizeGuideFlag.type = 'hidden';
    sizeGuideFlag.name = 'properties[_size_guide]';
    sizeGuideFlag.value = 'true';
    form.appendChild(sizeGuideFlag);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    sizeGuideFlag.remove();
    mirrorNativeAtcFeedback(form);
    return true;
  }

  // Relay the native add-to-cart button's submit feedback (spinner while the
  // /cart/add request runs, checkmark on success) onto the widget's button.
  // The spinner's timing mirrors the native button's is-loading class — added
  // synchronously during the requestSubmit above, cleared 1.4s after the
  // response. The checkmark does NOT mirror the native is-success class:
  // product-form.js adds that in its `finally`, i.e. even when /cart/add
  // FAILED (oversell, sold out mid-session). Success/failure is taken from the
  // cart events product-form dispatches before that — on:cart:add /
  // on:line-item:change (success) and on:cart:error (failure, which also
  // surfaces the server's message in the widget's own error line, since the
  // native error element is typically far off-screen from the widget).
  function mirrorNativeAtcFeedback(form) {
    const widgetBtn = sizeGuideForm.querySelector('.psg__atc-add');
    const nativeBtn = form.querySelector('[name="add"]');
    // No is-loading right after submit means the theme's AJAX handler didn't
    // run (e.g. after-ATC is set to a full page load) — nothing to mirror.
    if (!widgetBtn || !nativeBtn || !nativeBtn.classList.contains('is-loading')) return;

    let succeeded = false;
    const host = form.closest('product-form') || form;
    const onAdd = () => {
      succeeded = true;
      // Hosted in a modal → close it once the tick has registered, so the
      // cart drawer (which renders ~700ms after the add) isn't hidden behind
      // the modal overlay.
      const hostModal = sizeGuideForm.closest('modal-dialog[open]');
      if (hostModal && typeof hostModal.close === 'function') {
        setTimeout(() => hostModal.close(), 900);
      }
    };
    const onError = (evt) => {
      succeeded = false;
      showAtcError(evt.detail && evt.detail.error);
    };
    host.addEventListener('on:cart:add', onAdd, { once: true });
    host.addEventListener('on:line-item:change', onAdd, { once: true });
    host.addEventListener('on:cart:error', onError, { once: true });

    widgetBtn.classList.add('is-loading');

    const observer = new MutationObserver(() => {
      widgetBtn.classList.toggle(
        'is-success',
        succeeded && nativeBtn.classList.contains('is-success')
      );
      if (!nativeBtn.classList.contains('is-loading')) {
        widgetBtn.classList.remove('is-loading');
        widgetBtn.classList.remove('is-success');
        host.removeEventListener('on:cart:add', onAdd);
        host.removeEventListener('on:line-item:change', onAdd);
        host.removeEventListener('on:cart:error', onError);
        observer.disconnect();
      }
    });
    observer.observe(nativeBtn, { attributes: true, attributeFilter: ['class'] });
  }

  // Lowercase names of the selections still missing before the selection set
  // fully determines a variant: the Tab 1 recommendation (the size) plus every
  // option group the options panel renders (flap type, color, …). Empty means
  // complete. Without this check, getAtcVariant's skip-absent-filters behavior
  // would resolve a partial selection to "first surviving variant" — fine for
  // a price preview, wrong for a purchase.
  function getMissingSelections() {
    const missing = [];
    if (!recommendedVariant) missing.push('pet size');
    sizeGuideForm.querySelectorAll(OPTION_GROUP_SELECTOR).forEach((g) => {
      if (!sizeGuideSelections[g.dataset.optionName]) {
        missing.push(g.dataset.optionName.toLowerCase());
      }
    });
    return missing;
  }

  // Set the Add to cart button's state:
  //   'ready'      → enabled, "Add to cart"
  //   'soldout'    → disabled, greyed, "Sold out"
  //   'incomplete' → disabled, greyed, message names the missing selections
  //                  (falls back to the generic data-label-incomplete)
  function setAddToCartState(state, incompleteMessage) {
    const btn = sizeGuideForm.querySelector('.psg__atc-add');
    if (!btn) return;
    const ready = (state === 'ready');
    btn.disabled = !ready;
    btn.classList.toggle('psg__atc-add--soldout', state === 'soldout');
    const label = btn.querySelector('.psg__atc-add-label');
    if (label) {
      if (state === 'soldout') {
        label.textContent = btn.dataset.labelSoldout;
      } else if (state === 'incomplete') {
        label.textContent = incompleteMessage || btn.dataset.labelIncomplete || btn.dataset.labelSoldout;
      } else {
        label.textContent = btn.dataset.labelDefault;
      }
    }
  }

  // Repopulate the Add to Cart summary + price from current selections.
  // Safe to call even when the block isn't present — every lookup null-checks.
  function updateAddToCartTab() {
    const setText = (key, value) => {
      const el = sizeGuideForm.querySelector(`[data-atc="${key}"]`);
      if (el) el.textContent = value;
    };

    setText('size', (recommendedVariant && recommendedVariant.size) || '—');
    setText('flap', getSelectedFlapType() || '—');
    setText('color', getSelectedColor() || '—');
    setText('size-sub',
      recommendedVariant && recommendedVariant.max_pet_height != null
        ? `Fits pets up to ${recommendedVariant.max_pet_height}" shoulder height`
        : '');

    const qtyEl = sizeGuideForm.querySelector('[data-atc-qty]');
    if (qtyEl) qtyEl.textContent = atcQty;

    // Missing selections feed the disabled button's label so the user knows
    // exactly what to go back for.
    const missing = getMissingSelections();
    const complete = missing.length === 0;
    const variant = getAtcVariant();

    // A refreshed summary (tab entry / qty change) supersedes any prior save
    // and any stale add-to-cart error. Saving requires the same validator as
    // add-to-cart PLUS availability — committing a sold-out variant would let
    // the picker's availability-prune logic silently reselect something else.
    clearAtcError();
    const saveBtn = sizeGuideForm.querySelector('.psg__atc-save');
    if (saveBtn) {
      setSaveState(saveBtn, false);
      saveBtn.disabled = !(complete && variant && variant.available);
    }

    const priceEl = sizeGuideForm.querySelector('[data-atc-price]');
    if (priceEl) {
      priceEl.textContent = variant && variant.price != null
        ? formatMoney(variant.price * atcQty)
        : '—';
    }

    if (!complete || !variant) {
      const message = missing.length ? `Select ${missing.join(' & ')}` : null;
      setAddToCartState('incomplete', message);
    } else if (!variant.available) {
      setAddToCartState('soldout');
    } else {
      setAddToCartState('ready');
    }
  }

  // ── Modal hosting (#size-chart) ──────────────────────────────────────
  // When the product is size-guide-ready (data-psg-modal-host="true", set by
  // Liquid from size_guide.type), the widget takes over the #size-chart modal:
  // the live form MOVES into the modal content on open and back to its page
  // position on close. Moving (not cloning) keeps a single instance — no
  // duplicate ids, listeners intact, selections persisting across contexts.
  // The legacy size chart's cloner (sizechart-legacy.liquid) checks the same
  // attribute and leaves the modal alone when it's "true".
  function initModalHosting() {
    if (sizeGuideForm.dataset.psgModalHost !== 'true') return;

    const modal = document.getElementById('size-chart');
    const content = modal && modal.querySelector('.modal__content');
    if (!modal || !content) return;

    const modalWindow = modal.querySelector('.modal__window');
    const sectionRoot = sizeGuideForm.closest('.shopify-section') || sizeGuideForm.closest('.section');
    const home = { parent: sizeGuideForm.parentNode, next: sizeGuideForm.nextSibling };

    const host = () => {
      if (sizeGuideForm.parentNode === content) return;
      content.appendChild(sizeGuideForm);
      if (modalWindow) modalWindow.classList.add('modal__window--psg');
      if (sectionRoot) sectionRoot.classList.add('psg-section--hosted');
    };
    const unhost = () => {
      if (sizeGuideForm.parentNode !== content) return;
      home.parent.insertBefore(sizeGuideForm, home.next);
      if (modalWindow) modalWindow.classList.remove('modal__window--psg');
      if (sectionRoot) sectionRoot.classList.remove('psg-section--hosted');
    };

    // Pre-host on the opener's click in the CAPTURE phase — before the theme's
    // ModalOpener handler runs Modal.open(), whose focus trap snapshots the
    // modal's focusable elements. Hosting after that snapshot would leave the
    // trap knowing only the close button and lock Tab onto it.
    document.addEventListener('click', (evt) => {
      const opener = evt.target.closest('modal-opener[data-modal="size-chart"]');
      if (opener) host();
    }, { capture: true, signal });

    // The open attribute is the source of truth for restoring the widget to
    // the page — covers every close path (×, backdrop, Escape, programmatic).
    const openObserver = new MutationObserver(() => {
      if (modal.hasAttribute('open')) host();
      else unhost();
    });
    openObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
    signal.addEventListener('abort', () => openObserver.disconnect());
  }

  // ── Event wiring ─────────────────────────────────────────────────────
  // ── Submit: what happens depends on WHICH button submitted ────────────
  //   The panel's Add to cart button → commit variant + add to cart
  //   Anything else (footer / Enter) → navigation:
  //     Pet Sizing, no input  → no-op (button is muted)
  //     Pet Sizing, has input → advance (result is already live)
  //     Options, unselected   → no-op (button is muted)
  //     Other tabs            → advance
  // Identifying the cart path by event.submitter — not by tab position — means
  // a footer click can never reach the cart, regardless of block order.
  sizeGuideForm.addEventListener('submit', (event) => {
    event.preventDefault();

    // Fallback for browsers without event.submitter: treat it as an add only
    // when the visible panel is actually the Add to Cart panel.
    const isAtcSubmit = event.submitter
      ? !!event.submitter.closest('.psg__atc-add')
      : !!sizeGuideForm.querySelector('.psg__panel:not(.psg__panel--hidden) .psg__atc');

    // Commit the chosen variant to the page, then add it to the cart via the
    // theme's product form. The button is disabled when the selection set is
    // incomplete or the variant is sold out, so a submit here means a
    // fully-resolved, available variant — but guard anyway.
    if (isAtcSubmit) {
      // An add is already in flight (spinner showing) — ignore repeat clicks.
      const atcBtn = sizeGuideForm.querySelector('.psg__atc-add');
      if (atcBtn && atcBtn.classList.contains('is-loading')) return;

      if (getMissingSelections().length) return;
      const variant = getAtcVariant();
      if (!variant || !variant.available) return;
      clearAtcError();
      commitToVariantPicker(variant);
      addToCartViaForm(variant, atcQty);
      return;
    }

    if (activePanelIsPetSizing()) {
      if (!tab1HasInput()) return;
      advanceTab();
      return;
    }

    // Otherwise: advance to next tab — unless the visible (sub-)panel still
    // needs a selection (button is muted; mirror Tab 1's inert behavior).
    if (getPendingSelectionName()) return;
    advanceTab();
  }, { signal });

  // ── Input changes drive the live recommendation ──────────────────────
  // Compute on every select/checkbox change. Height or width present →
  // render the recommendation. Neither present → clear the result card
  // (the .psg__result:empty rule hides it).
  sizeGuideForm.addEventListener('change', (event) => {
    if (!event.target.matches('select, input[type="checkbox"]')) return;

    const heightSelect = sizeGuideForm.querySelector('#product-size-guide-height');
    const widthSelect = sizeGuideForm.querySelector('#product-size-guide-width');
    const formData = new FormData(sizeGuideForm);
    const heightRaw = formData.get('height');
    const widthRaw = formData.get('width');
    const inputs = {
      height: heightRaw ? Number(heightRaw) : 0,
      width: widthRaw ? Number(widthRaw) : 0,
      heightLabel: heightRaw ? heightSelect.selectedOptions[0].text : '',
      widthLabel: widthRaw ? widthSelect.selectedOptions[0].text : '',
      limited: formData.get('limited') === '1',
    };

    if (inputs.height || inputs.width) {
      if (sizingDataMissing(inputs)) {
        recommendedVariant = null;
        renderDataUnavailable();
      } else {
        const matches = findSize(inputs.height, inputs.width, inputs.limited, sizeGuideData);
        recommendedVariant = matches[0] || null;
        renderResult(recommendedVariant, inputs);
      }
    } else {
      recommendedVariant = null;
      if (sizeGuideResult) sizeGuideResult.innerHTML = '';
      updateDimensionsTab(null);
    }

    renderColorSwatches();
    updateButtonLabel();
  }, { signal });

  // ── Back button: retreat to previous tab ─────────────────────────────
  if (sizeGuideBackBtn) {
    sizeGuideBackBtn.addEventListener('click', retreatTab, { signal });
  }

  // The footer button is type="submit"; in "Start over" mode intercept its click
  // and cancel the submit so it resets instead of triggering add-to-cart.
  sizeGuideNextBtn.addEventListener('click', (event) => {
    if (sizeGuideNextBtn.classList.contains('psg__btn--restart')) {
      event.preventDefault();
      restartWidget();
    }
  }, { signal });

  sizeGuideForm.addEventListener('click', (event) => {
    const subtab = event.target.closest('.psg__subtab');
    if (subtab) { handleSubtabClick(subtab); return; }

    const swatch = event.target.closest('.psg__color-swatch');
    if (swatch) { handleSwatchClick(swatch); return; }

    const optionBtn = event.target.closest('.psg__option-btn');
    if (optionBtn) { handleOptionBtnClick(optionBtn); return; }

    const optionDetail = event.target.closest('.psg__option-detail');
    if (optionDetail) { handleOptionDetailClick(optionDetail); return; }

    if (event.target.closest('[data-atc-qty-dec]')) { setAtcQty(atcQty - 1); return; }
    if (event.target.closest('[data-atc-qty-inc]')) { setAtcQty(atcQty + 1); return; }

    // Trust badges need no JS here: both Warranty (the widget's own
    // #psg-warranty modal) and Returns (#refund-policy) open through the
    // theme's <modal-opener> element, whose modal stack handles being
    // triggered from inside another modal.

    // Save and continue → commit the chosen variant to the page's native
    // variant picker (updates price/media/variant id, NOT the cart), then show
    // the "Product Choice Saved" confirmation.
    const saveBtn = event.target.closest('.psg__atc-save');
    if (saveBtn) {
      commitToVariantPicker(getAtcVariant());
      setSaveState(saveBtn, true);
      return;
    }
  }, { signal });

  // Description cards are role="button" — activate them with Enter/Space like a
  // native button (Space would otherwise scroll the page).
  sizeGuideForm.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    const detail = event.target.closest('.psg__option-detail');
    if (!detail) return;
    event.preventDefault();
    handleOptionDetailClick(detail);
  }, { signal });

  // Theme editor: selecting a block in the sidebar shows that block's tab, so
  // an admin can edit any step without operating the widget to reach it. Each
  // panel carries block.shopify_attributes, so the event's target is the panel
  // itself. The editor is the only thing that fires this event.
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest && event.target.closest('.psg__panel[data-tab-index]');
    if (!panel || !sizeGuideForm.contains(panel)) return;
    gotoTab(Number(panel.dataset.tabIndex));
  }, { signal });

  // The warranty modal must not live inside transformed/scrolling ancestors:
  // when the widget is hosted in the size-chart modal, .modal__window's scale()
  // transform would become the fixed-position containing block and clip it.
  // Park it at document.body once, where every other page-level modal lives.
  // On teardown it's removed — the re-rendered section brings its own, and a
  // stale copy left in <body> would duplicate the id.
  const psgWarrantyModal = document.getElementById('psg-warranty');
  if (psgWarrantyModal && psgWarrantyModal.parentElement !== document.body) {
    document.body.appendChild(psgWarrantyModal);
    signal.addEventListener('abort', () => psgWarrantyModal.remove());
  }

  // ── Live health checks ───────────────────────────────────────────────
  // Passive, run once at startup: they read the widget's own data and markup,
  // never drive it. Silent when everything is in place, so any [psg] line in
  // the console means something needs attention. Broken states (the widget
  // can't run) log console.error where they're detected above; degraded
  // states (it runs, but part of it is inert) log one console.warn each here.

  // Per-variant data the widget reads. A gap leaves part of it inert: a
  // sizing input that can't recommend, limited mobility that can't adjust, or
  // a Dimensions row stuck on "—". `source` names what to fill in the admin.
  const DATA_CHECKS = [
    { source: 'a "Size" product option', missing: v => v.size == null },
    { source: 'compatibility.max_pet_shoulder_height', missing: v => v.max_pet_height == null },
    { source: 'specs.flap (width + height)', missing: v => !v.flap || v.flap.width == null || v.flap.height == null },
    { source: 'specs.min_step_over + max_step_over', missing: v => v.min_step == null || v.max_step == null },
    { source: 'specs.rough_cut_out', missing: v => !v.cutout || v.cutout.width == null || v.cutout.height == null },
    { source: 'specs.outside_frame_dimensions', missing: v => !v.frame || v.frame.width == null || v.frame.height == null },
    { source: 'compatibility.min/max_compatible_thickness', missing: v => v.min_compatible_thickness == null && v.max_compatible_thickness == null },
  ];

  function reportHealth() {
    if (!sizeGuideForm.querySelector('.psg__tab')) {
      console.warn('[psg] No size guide steps render for this product — check each block\'s "Show for product types" setting against the product\'s size_guide.type.');
    }

    const variants = sizeGuideData.variants;
    if (!variants.length) {
      console.warn('[psg] The widget data has no variants — the size guide can\'t recommend anything.');
      return;
    }

    const gaps = DATA_CHECKS
      .map(({ source, missing }) => ({ source, count: variants.filter(missing).length }))
      .filter(gap => gap.count > 0);
    if (gaps.length) {
      console.warn(
        '[psg] Sizing data gaps on this product — parts of the size guide stay inactive or show "—":\n' +
        gaps.map(gap => `  ${gap.source}: missing on ${gap.count} of ${variants.length} variants`).join('\n')
      );
    }
  }

  // Widget loads with no input → make sure the button starts muted
  // and the color swatches render with the full unfiltered set.
  // Broken data payload → park the widget on the error card instead
  // (the footer reads "Start over", which safely re-shows the card) and
  // withdraw the modal-host claim so the legacy size chart keeps that modal.
  if (sizeGuideDataOk) {
    reportHealth();
    updateButtonLabel();
    renderColorSwatches();
    updateAddToCartTab();
    initModalHosting();
  } else {
    sizeGuideForm.dataset.psgModalHost = 'false';
    showFallbackPanel();
  }
})();
